import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const getYouTubeEmbedUrl = (url) => {
  try {
    const urlObj = new URL(url);
    
    // Handle youtu.be short URLs
    if (urlObj.hostname === 'youtu.be') {
      const videoId = urlObj.pathname.split('/')[1];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
    
    // Handle full YouTube URLs
    if (['www.youtube.com', 'youtube.com'].includes(urlObj.hostname)) {
      const videoId = urlObj.searchParams.get('v');
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
      }
    }
    
    return null;
  } catch {
    return null;
  }
};

const VideoEmbed = ({ url, index }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const embedUrl = getYouTubeEmbedUrl(url);

  useEffect(() => {
    if (embedUrl) {
      setIsLoaded(false);
      setHasError(false);
    }
  }, [embedUrl]);

  if (!embedUrl) {
    return (
      <div className="p-4 bg-card rounded-lg text-muted">
        Invalid YouTube URL: {url}
      </div>
    );
  }

  return (
    <div className="max-w-[600px]">
      <div className="relative" style={{ paddingBottom: '56.25%' }}>
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-card rounded-lg">
            <div className="animate-pulse">Loading video...</div>
          </div>
        )}
        
        {hasError ? (
          <div className="absolute inset-0 p-4 bg-card rounded-lg flex flex-col items-center justify-center">
            <p className="text-muted mb-2">Video unavailable</p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Watch on YouTube
            </a>
          </div>
        ) : (
          <iframe
            src={embedUrl}
            title={`Video ${index + 1}`}
            className={`absolute top-0 left-0 w-full h-full rounded-lg ${isLoaded ? 'visible' : 'invisible'}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sandbox="allow-scripts allow-same-origin allow-presentation"
            onLoad={() => setIsLoaded(true)}
            onError={() => setHasError(true)}
          />
        )}
      </div>
      {isLoaded && !hasError && (
        <div className="mt-2 text-sm text-muted break-all">
          {url}
        </div>
      )}
    </div>
  );
};

VideoEmbed.propTypes = {
  url: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
};

export default VideoEmbed;
